import { parseArtifactPayload } from 'breeze-engine-core';
import { convertFieldValueAvroToJson } from 'rpc-client-utils';
import { DisplayOnlyChirpContentBlockTypename } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { isFileArtifactTool, isArtifactsPath, parseArtifactFilename, deriveArtifactAction } from './artifactFilename';
function isAvroFieldValue(value) {
  if (typeof value !== 'object' || value === null) return false;
  const typename = value.__typename;
  return typeof typename === 'string' && typename.startsWith('com.hubspot.chirp.ext.models.');
}
function resolveInputRecord(input) {
  if (input == null) return null;
  const resolved = isAvroFieldValue(input) ? convertFieldValueAvroToJson(input) : input;
  if (resolved == null || typeof resolved !== 'object') return null;
  return resolved;
}
function getFilePath(record) {
  const candidates = ['filePath', 'file_path', 'path', 'filename', 'fileName'];
  for (const key of candidates) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}
function getFileContent(record) {
  const value = record.content;
  return typeof value === 'string' ? value : null;
}

// Payload-bearing artifact types store a JSON document in the file body whose
// shape matches the corresponding ArtifactPayload. The artifact type is derived
// from the filename, so inject it as the `kind` discriminator that
// parseArtifactPayload dispatches on rather than trusting the file to carry it.
function parsePayloadFromContent(artifactType, content) {
  if (content === null) return undefined;
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch (_unused) {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  return parseArtifactPayload(Object.assign({}, parsed, {
    kind: artifactType
  }));
}
function parseArtifactWriteMetadata(pair, artifactPath) {
  var _pair$output;
  if (((_pair$output = pair.output) === null || _pair$output === void 0 ? void 0 : _pair$output.type) !== 'TEXT' || pair.output.isError) return undefined;
  let parsed;
  try {
    parsed = JSON.parse(pair.output.content);
  } catch (_unused2) {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  const artifactWrites = parsed.artifactWrites;
  if (!Array.isArray(artifactWrites)) return undefined;
  for (const metadata of artifactWrites) {
    if (typeof metadata !== 'object' || metadata === null) continue;
    const record = metadata;
    const metadataPath = record.fileName;
    if (typeof metadataPath !== 'string' || !artifactPathsMatch(metadataPath, artifactPath) || typeof record.id !== 'number' || typeof record.portalId !== 'number' || typeof record.conversationId !== 'number' || record.userId !== undefined && typeof record.userId !== 'number' || typeof record.agentId !== 'string' || typeof record.title !== 'string' || typeof record.artifactType !== 'number' || record.artifactTypeName !== undefined && typeof record.artifactTypeName !== 'string' || typeof record.status !== 'number' || record.currentVersionId !== undefined && typeof record.currentVersionId !== 'number' || typeof record.currentVersion !== 'number' || typeof record.createdAt !== 'number' || typeof record.updatedAt !== 'number' || typeof record.versionId !== 'number' || typeof record.action !== 'string') {
      continue;
    }
    return Object.assign({
      fileName: metadataPath,
      id: record.id,
      portalId: record.portalId,
      conversationId: record.conversationId
    }, typeof record.userId === 'number' && {
      userId: record.userId
    }, {
      agentId: record.agentId,
      title: record.title,
      artifactType: record.artifactType
    }, typeof record.artifactTypeName === 'string' && {
      artifactTypeName: record.artifactTypeName
    }, {
      status: record.status
    }, typeof record.currentVersionId === 'number' && {
      currentVersionId: record.currentVersionId
    }, {
      currentVersion: record.currentVersion,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      versionId: record.versionId,
      action: record.action
    });
  }
  return undefined;
}
function artifactPathsMatch(left, right) {
  const normalize = path => path.replace(/\\/g, '/').replace(/^\/?artifacts\//, '');
  return normalize(left) === normalize(right);
}
export function extractArtifactToolCalls(pairs, requireSuccessfulOutput = false) {
  const detected = [];
  for (const pair of pairs) {
    var _pair$output2, _pair$output3;
    if (!isFileArtifactTool(pair.input.name)) continue;
    if (((_pair$output2 = pair.output) === null || _pair$output2 === void 0 ? void 0 : _pair$output2.type) === 'TEXT' && pair.output.isError) continue;
    if (requireSuccessfulOutput && (((_pair$output3 = pair.output) === null || _pair$output3 === void 0 ? void 0 : _pair$output3.type) !== 'TEXT' || pair.output.isError)) {
      continue;
    }
    const record = resolveInputRecord(pair.input.input);
    if (record === null) continue;
    const filePath = getFilePath(record);
    if (filePath === null) continue;
    if (!isArtifactsPath(filePath)) continue;
    detected.push({
      path: filePath,
      toolName: pair.input.name,
      toolUseId: pair.input.id,
      content: getFileContent(record),
      writeMetadata: parseArtifactWriteMetadata(pair, filePath)
    });
  }
  return detected;
}
export function buildArtifactBlocksFromDetected(detected) {
  if (detected.length === 0) return [];
  const byPath = new Map();
  for (const item of detected) {
    byPath.set(item.path, item);
  }
  const blocks = [];
  for (const item of byPath.values()) {
    var _metadata$artifactTyp, _metadata$title, _metadata$action;
    const parsed = parseArtifactFilename(item.path);
    if (parsed === null) continue;
    const payload = parsePayloadFromContent(parsed.dataType, item.content);
    const metadata = item.writeMetadata;
    blocks.push(Object.assign({
      type: 'ARTIFACT',
      artifactType: (_metadata$artifactTyp = metadata === null || metadata === void 0 ? void 0 : metadata.artifactTypeName) !== null && _metadata$artifactTyp !== void 0 ? _metadata$artifactTyp : parsed.dataType,
      name: (_metadata$title = metadata === null || metadata === void 0 ? void 0 : metadata.title) !== null && _metadata$title !== void 0 ? _metadata$title : parsed.title,
      path: item.path,
      action: (_metadata$action = metadata === null || metadata === void 0 ? void 0 : metadata.action) !== null && _metadata$action !== void 0 ? _metadata$action : deriveArtifactAction(item.toolName),
      source: 'filesystem'
    }, metadata !== undefined && {
      artifactId: metadata.id,
      versionId: metadata.versionId,
      version: metadata.currentVersion,
      artifactMetadata: metadata
    }, payload !== undefined && {
      payload
    }));
  }
  return blocks;
}
export function buildArtifactBlocksFromToolCalls(pairs) {
  return buildArtifactBlocksFromDetected(extractArtifactToolCalls(pairs));
}
function getDisplayBlockItems(value) {
  if (Array.isArray(value)) {
    return value.filter(item => typeof item === 'object' && item !== null);
  }
  if (isAvroFieldValue(value)) {
    const decoded = convertFieldValueAvroToJson(value);
    if (Array.isArray(decoded)) {
      return decoded.filter(item => typeof item === 'object' && item !== null);
    }
  }
  return [];
}
function parseArtifactWriteEntryRecord(record) {
  if (record.entryType !== 'ARTIFACT_WRITE') return null;
  if (typeof record.fileName !== 'string' || typeof record.id !== 'number' || typeof record.portalId !== 'number' || typeof record.conversationId !== 'number' || record.userId !== undefined && typeof record.userId !== 'number' || typeof record.agentId !== 'string' || typeof record.title !== 'string' || typeof record.artifactType !== 'number' || record.artifactTypeName !== undefined && typeof record.artifactTypeName !== 'string' || typeof record.status !== 'number' || record.currentVersionId !== undefined && typeof record.currentVersionId !== 'number' || typeof record.currentVersion !== 'number' || typeof record.createdAt !== 'number' || typeof record.updatedAt !== 'number' || typeof record.versionId !== 'number' || typeof record.action !== 'string') {
    return null;
  }
  return {
    metadata: Object.assign({
      fileName: record.fileName,
      id: record.id,
      portalId: record.portalId,
      conversationId: record.conversationId
    }, typeof record.userId === 'number' && {
      userId: record.userId
    }, {
      agentId: record.agentId,
      title: record.title,
      artifactType: record.artifactType
    }, typeof record.artifactTypeName === 'string' && {
      artifactTypeName: record.artifactTypeName
    }, {
      status: record.status
    }, typeof record.currentVersionId === 'number' && {
      currentVersionId: record.currentVersionId
    }, {
      currentVersion: record.currentVersion,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      versionId: record.versionId,
      action: record.action
    }),
    contents: typeof record.contents === 'string' ? record.contents : null
  };
}
export function extractArtifactWriteEntries(blocks) {
  const entries = [];
  const consumedBlocks = new Set();
  for (const block of blocks) {
    if (block.__typename !== DisplayOnlyChirpContentBlockTypename) continue;
    const displayOnly = block;
    if (!displayOnly.value) continue;
    const items = getDisplayBlockItems(displayOnly.value);
    // Only consume the block when every item claims entryType ARTIFACT_WRITE.
    // A mixed block would silently drop any non-ARTIFACT_WRITE items if we
    // consumed it here, since those items are excluded from the normal
    // display-only rendering path. The orchestration worker currently emits
    // one dedicated block per artifact write, so this is always all-or-nothing.
    if (items.length === 0 || !items.every(item => item.entryType === 'ARTIFACT_WRITE')) {
      continue;
    }
    const blockEntries = [];
    for (const item of items) {
      const entry = parseArtifactWriteEntryRecord(item);
      if (entry !== null) {
        blockEntries.push(entry);
      }
    }
    if (blockEntries.length > 0) {
      entries.push(...blockEntries);
      consumedBlocks.add(block);
    }
  }
  return {
    entries,
    consumedBlocks
  };
}
export function buildArtifactBlocksFromWriteEntries(entries) {
  return entries.flatMap(({
    metadata,
    contents
  }) => {
    var _metadata$fileName, _ref, _metadata$artifactTyp2;
    const fileName = (_metadata$fileName = metadata.fileName) !== null && _metadata$fileName !== void 0 ? _metadata$fileName : '';
    if (!fileName) return [];
    const filePath = `/artifacts/${fileName}`;
    const parsed = parseArtifactFilename(filePath);
    const payload = parsed !== null ? parsePayloadFromContent(parsed.dataType, contents) : undefined;
    return [Object.assign({
      type: 'ARTIFACT',
      artifactType: (_ref = (_metadata$artifactTyp2 = metadata.artifactTypeName) !== null && _metadata$artifactTyp2 !== void 0 ? _metadata$artifactTyp2 : parsed === null || parsed === void 0 ? void 0 : parsed.dataType) !== null && _ref !== void 0 ? _ref : fileName,
      name: metadata.title,
      path: filePath,
      action: metadata.action,
      source: 'filesystem',
      artifactId: metadata.id,
      versionId: metadata.versionId,
      version: metadata.currentVersion,
      artifactMetadata: metadata
    }, payload !== undefined && {
      payload
    })];
  });
}